/**
 * Card Product Gallery
 * Handles image navigation with left/right arrows and touch swipe on product cards
 */

if (!customElements.get('card-gallery')) {
  class CardGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.images = [];
      this.prevButton = null;
      this.nextButton = null;
      this.touchTarget = null;
      this.productLink = null;
      
      // Touch/swipe tracking
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchEndX = 0;
      this._touchEndY = 0;
      this._isSwiping = false;
      this._didSwipe = false;
      this._touchedTimeout = null;
      
      // Bind methods
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchTargetClick = this.handleTouchTargetClick.bind(this);
    }

    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.card__gallery-image'));
      if (!this.images.length) return;

      this.prevButton = this.querySelector('.card__gallery-btn--prev');
      this.nextButton = this.querySelector('.card__gallery-btn--next');
      
      // Get the touch target element (the media container that actually receives touch events)
      // Note: card-gallery has display:contents so it doesn't receive events directly
      this.touchTarget = this.querySelector('.card__media') || this.querySelector('.card__gallery-image-stack');

      const initialIndex = this.images.findIndex((image) => image.classList.contains('is-visible'));
      this.currentIndex = initialIndex >= 0 ? initialIndex : 0;

      this.updateVisibility();
      this.updateButtonStates();

      if (this.images.length <= 1) return;

      // Preload all images so transitions don't flash blank frames
      this.preloadAllImages();

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }
      
      // Setup touch/swipe handlers for mobile
      this.setupTouchHandlers();

      this.updateButtonStates();
    }
    
    setupTouchHandlers() {
      // Add touch event listeners for swipe navigation on touch devices
      // We attach to the touchTarget (media container) since card-gallery has display:contents
      // and doesn't receive touch events directly
      if (!this.touchTarget) return;
      
      // Find the product link for tap-to-navigate functionality
      const card = this.closest('.card-wrapper') || this.closest('.card');
      this.productLink = card?.querySelector('a[href]')?.href;
      
      this.touchTarget.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.touchTarget.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.touchTarget.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      
      // Add click handler to navigate on tap (when not swiping)
      this.touchTarget.addEventListener('click', this.handleTouchTargetClick);
    }
    
    handleTouchTargetClick(event) {
      // If we just swiped, don't navigate
      if (this._didSwipe) {
        event.preventDefault();
        return;
      }
      
      // Navigate to product page on tap
      if (this.productLink) {
        window.location.href = this.productLink;
      }
    }

    handleTouchStart(event) {
      const touch = event.touches[0];
      this._touchStartX = touch.clientX;
      this._touchStartY = touch.clientY;
      this._touchEndX = touch.clientX;
      this._touchEndY = touch.clientY;
      this._isSwiping = false;
      this._didSwipe = false;
    }

    handleTouchMove(event) {
      if (!event.touches.length) return;
      
      const touch = event.touches[0];
      this._touchEndX = touch.clientX;
      this._touchEndY = touch.clientY;
      
      // Calculate the difference
      const deltaX = Math.abs(this._touchEndX - this._touchStartX);
      const deltaY = Math.abs(this._touchEndY - this._touchStartY);
      
      // If horizontal movement is greater than vertical, we're swiping horizontally
      // Prevent default to stop vertical scroll and enable smooth horizontal swipe
      if (deltaX > deltaY && deltaX > 10) {
        this._isSwiping = true;
        event.preventDefault();
      }
    }

    handleTouchEnd() {
      const minSwipeDistance = 50;
      const deltaX = this._touchEndX - this._touchStartX;
      const deltaY = Math.abs(this._touchEndY - this._touchStartY);
      const absX = Math.abs(deltaX);
      
      // Only trigger if horizontal distance exceeds threshold and is greater than vertical
      if (absX >= minSwipeDistance && absX > deltaY) {
        this._didSwipe = true;
        if (deltaX < 0 && this.currentIndex < this.images.length - 1) {
          this.goToImage(this.currentIndex + 1);
        } else if (deltaX > 0 && this.currentIndex > 0) {
          this.goToImage(this.currentIndex - 1);
        }
        // Show controls after swipe
        this.showControls();
      }
      
      // Reset tracking values
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchEndX = 0;
      this._touchEndY = 0;
      this._isSwiping = false;
    }
    
    showControls() {
      // Add class to show controls
      this.classList.add('is-touched');
      
      // Clear any existing timeout
      if (this._touchedTimeout) {
        clearTimeout(this._touchedTimeout);
      }
      
      // Hide controls after 2 seconds of inactivity
      this._touchedTimeout = setTimeout(() => {
        this.classList.remove('is-touched');
      }, 2000);
    }

    preloadAllImages() {
      this.images.forEach((imageEl) => {
        if (imageEl && imageEl.src) {
          const img = new Image();
          img.src = imageEl.currentSrc || imageEl.src;
        }
      });
    }

    handlePrevClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.currentIndex > 0) {
        this.goToImage(this.currentIndex - 1);
      }
    }

    handleNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.currentIndex < this.images.length - 1) {
        this.goToImage(this.currentIndex + 1);
      }
    }

    goToImage(index) {
      if (index < 0 || index >= this.images.length || index === this.currentIndex) return;
      this.currentIndex = index;
      this.updateVisibility();
      this.updateButtonStates();
    }

    updateVisibility() {
      this.images.forEach((image, idx) => {
        const isActive = idx === this.currentIndex;
        image.classList.toggle('is-visible', isActive);
        if (isActive) {
          image.removeAttribute('aria-hidden');
        } else {
          image.setAttribute('aria-hidden', 'true');
        }
      });
    }

    updateButtonStates() {
      if (this.prevButton) {
        this.prevButton.disabled = this.images.length <= 1 || this.currentIndex === 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled =
          this.images.length <= 1 || this.currentIndex === this.images.length - 1;
      }
    }

    disconnectedCallback() {
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }
      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }
      
      // Remove touch event listeners from the touch target
      if (this.touchTarget) {
        this.touchTarget.removeEventListener('touchstart', this.handleTouchStart);
        this.touchTarget.removeEventListener('touchmove', this.handleTouchMove);
        this.touchTarget.removeEventListener('touchend', this.handleTouchEnd);
        this.touchTarget.removeEventListener('click', this.handleTouchTargetClick);
      }
      
      // Clear any pending timeout
      if (this._touchedTimeout) {
        clearTimeout(this._touchedTimeout);
      }
    }
  }

  customElements.define('card-gallery', CardGallery);
}
